import * as React from "react";
import debounce from "lodash-es/debounce";
import { UpdateCalendar } from "../common/communication.base";
import { NclCalendar } from "../common/components.ncl";
import { AcquireControl, K2ComponentState, withContext, WithContextPlacementProps, StyleHelper } from "./k2hoc";
import FullCalendar, { DatesSetArg, CalendarApi, EventClickArg } from "@fullcalendar/react";
import { EventApi, Duration, DayCellContentArg, MountArg, EventInput } from "@fullcalendar/core";
import { EventResizeDoneArg } from "@fullcalendar/interaction";
import "@fullcalendar/core/locales/cs.js";
import DatePicker, { registerLocale } from "react-datepicker";
import { cs } from "date-fns/locale";
import { K2Img } from "./K2Image";
import ResizeObserver from "resize-observer-polyfill";
import { K2CalendarCommon } from "./common/K2Calendar";
import { endDateCorrection, ExtendedProps, GetEvents, K2CalendarStyles } from "./common/CalendarFunctions";
registerLocale("cs", cs);

interface CalendarState extends K2ComponentState<UpdateCalendar> {
  Height: number;
  Width: number;
  CalendarAspectRatio: number;
  CalendarContentHeight: number;
  isDatepickerOpen: boolean;
  events: EventInput[];
}

class _K2Calendar extends React.PureComponent<WithContextPlacementProps, CalendarState> {
  static displayName = `K2Calendar`;
  private control: NclCalendar;
  private calendarApi: CalendarApi;
  private datePicker = React.createRef<HTMLDivElement>();
  private wrapper = React.createRef<HTMLDivElement>();

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, () => {
      return true;
    }) as NclCalendar;
    this.state = {
      data: this.control.init(this) as UpdateCalendar,
      vcxVersion: -1,
      Height: 0,
      Width: 0,
      CalendarAspectRatio: 2,
      CalendarContentHeight: 100,
      isDatepickerOpen: false,
      events: [],
    };

    if (process.env.NODE_ENV === "development") {
      window.addEventListener("contextmenu", (e) => e.preventDefault());
    }

    let colors = {
      AccentBaseColorBck: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.AccentBaseColorBck),
      GridRulerColorBck: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.GridRulerColorBck),
      BaseColorBck1: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1),
      ContentColorBck1: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.ContentColorBck1),
      ContentChangeDecorateColorFrg: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.ContentChangeDecorateColorFrg),
      BaseColorFrg1: this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorFrg1),
    };

    this.props.renderer.renderStatic(K2CalendarStyles(colors));
  }

  updateState(state: UpdateCalendar) {
    const lEvents: EventInput[] = GetEvents(state.toJS() as UpdateCalendar);

    lEvents.map((event) => {
      (event.extendedProps as ExtendedProps).StateIconJSX = (
        <div className="fc-state-icon">
          <K2Img vcx={this.control.VCX} glyphId={event.extendedProps.StateGlyphId} width={15} height={15} />
        </div>
      );
    });

    this.setState({ data: state, events: lEvents });
  }

  disableScroll = (e: WheelEvent) => {
    if (this.calendarApi.view.type === "dayGrid" || this.calendarApi.view.type === "dayGridMonth") {
      e.preventDefault();
    }
  };

  componentDidMount() {
    this.wrapper.current.addEventListener("wheel", this.disableScroll);
    const ro = new ResizeObserver(() => {
      this.calendarApi.updateSize();
    });

    ro.observe(this.wrapper.current);
  }

  getApi = (calendarComponentRef: React.RefObject<FullCalendar>) => {
    this.calendarApi = calendarComponentRef.current.getApi();
  };

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  componentWillUnmount() {
    this.wrapper.current.removeEventListener("wheel", this.disableScroll);
    this.control.willUnMount(true);
    this.control = null;
  }

  onChange = (date: Date) => {
    this.calendarApi.changeView("timeGridDay", date);
    this.setState({ isDatepickerOpen: false });
  };

  contextMenuCallback = (args: MountArg<DayCellContentArg>) => {
    this.control.contextMenu(args.date);
  };

  showDatepicker = (left: number, top: number) => {
    this.datePicker.current.style.left = left + "px";
    this.datePicker.current.style.top = top + "px";
    this.setState({ isDatepickerOpen: true });
  };

  render() {
    return (
      <div
        ref={this.wrapper}
        style={StyleHelper(this.control, { alignSelf: "auto", flexDirection: "column", ...this.props.style })}
        className={this.props.renderer.renderFontRule(this.control.VCX.LabelControl.Font, this.control.VCX)}
      >
        <div ref={this.datePicker} style={{ position: "fixed", zIndex: 2 }}>
          {this.state.isDatepickerOpen && (
            <DatePicker
              dateFormatCalendar="LLLL"
              locale="cs"
              startOpen
              showYearDropdown
              onChange={this.onChange}
              onCalendarClose={() => this.setState({ isDatepickerOpen: false })}
            />
          )}
        </div>
        <K2CalendarCommon
          handleAppointmentResize={this.handleAppointmentResize}
          handleDatesRender={this.handleDatesRender}
          handleDrop={this.handleDrop}
          handleEventClick={this.handleEventClick}
          handleSettingButonClick={this.handleSettingButonClick}
          contextMenuCallback={this.contextMenuCallback}
          lEvents={this.state.events}
          hintBackgroundColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.DataChangeColorBck)}
          hintBorderColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.ContentFrame1)}
          getApi={this.getApi}
          deleteButton={<K2Img glyphId="wui*close" vcx={this.control.VCX} height={15} width={15} strokeColor="currentColor" />}
          initialStartDate={this.state.data.InitialStartDate}
          initialView={this.state.data.InitialView}
          showDatepicker={this.showDatepicker}
          activeStartDate={this.state.data.ActiveStartDate}
          activeEndDate={this.state.data.ActiveEndDate}
        />
      </div>
    );
  }

  handleDrop = (arg: { el: HTMLElement; event: EventApi; oldEvent: EventApi; delta: Duration; revert: () => void; jsEvent: Event }) => {
    this.control.DragAnDropAppointment(
      arg.event.extendedProps.SerieUI,
      arg.event.extendedProps.K2Pk,
      arg.oldEvent.start,
      arg.event.start,
      endDateCorrection(arg.event.end, arg.event.allDay, "subtract", arg.event.start),
      arg.event.extendedProps.AppointmentId,
      arg.event.extendedProps.AppointmentItemType
    );
  };

  handleAppointmentResize = (arg: EventResizeDoneArg) => {
    this.control.DragAnDropAppointment(
      arg.event.extendedProps.SerieUI,
      arg.event.extendedProps.K2Pk,
      arg.oldEvent.start,
      arg.event.start,
      endDateCorrection(arg.event.end, arg.event.allDay, "subtract", arg.event.start),
      arg.event.extendedProps.AppointmentId,
      arg.event.extendedProps.AppointmentItemType
    );
  };

  handleDatesRender = (arg: DatesSetArg) => {
    this.debounce(arg);
  };

  debounce = debounce((arg: DatesSetArg) => {
    if (!this.control) return;
    this.control.ChangeActiveDateRange(arg.view.activeStart, arg.view.activeEnd);
  }, 250);

  handleSettingButonClick = () => {
    this.control.executeShowSetting();
  };

  handleEventClick = (arg: EventClickArg) => {
    if ((arg.jsEvent.target as HTMLElement).className === "fc-button-delete-event") {
      if (arg.event.extendedProps.K2Pk && arg.event.extendedProps.K2Pk != "") {
        this.control.executeDeleteAppointment(arg.event.extendedProps.SerieUI, arg.event.extendedProps.K2Pk);
      } else {
        this.control.executeDeleteOnlineAppointment(
          arg.event.extendedProps.SerieUI,
          arg.event.extendedProps.AppointmentId,
          arg.event.extendedProps.AppointmentItemType
        );
      }
    } else {
      if (arg.event.extendedProps.K2Pk && arg.event.extendedProps.K2Pk != "") {
        this.control.executeShowDocumentByK2PK(arg.event.extendedProps.SerieUI, arg.event.extendedProps.K2Pk);
      } else {
        this.control.executeEditOnlineAppointment(
          arg.event.extendedProps.SerieUI,
          arg.event.extendedProps.AppointmentId,
          arg.event.extendedProps.AppointmentItemType
        );
      }
    }
  };
}

export const K2Calendar = withContext(_K2Calendar);
